import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    ChevronLeft, Image, Sparkles, Download, Trash2, Star, Loader2,
    BookOpen, Palette, Type, Layout, Wand2, Crown, Lock, Check,
    ImagePlus, RefreshCw, Eye, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { coversApi, booksApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const GENRE_OPTIONS = [
    'Fantasy', 'Sci-Fi', 'Romance', 'Thriller', 'Mystery', 
    'Horror', 'Literary', 'YA', 'Nonfiction'
];

const FORMAT_OPTIONS = [
    { id: 'ebook', name: 'eBook', description: 'Digital format (1600×2560)' },
    { id: 'paperback_6x9', name: 'Paperback 6×9"', description: 'Standard trade paperback' },
    { id: 'paperback_5x8', name: 'Paperback 5×8"', description: 'Mass market size' },
    { id: 'hardcover_6x9', name: 'Hardcover 6×9"', description: 'Premium hardcover' }
];

const COVER_TYPES = [
    { id: 'front', name: 'Front Cover', icon: BookOpen, cost: 5, minTier: 'free' },
    { id: 'back', name: 'Back Cover', icon: Layout, cost: 3, minTier: 'author' },
    { id: 'spine', name: 'Spine', icon: Type, cost: 2, minTier: 'author' },
    { id: 'full', name: 'Full Wraparound', icon: ImagePlus, cost: 8, minTier: 'author' }
];

const CoverDesigner = ({ onBack, onNavigate }) => {
    const { user } = useAuth();
    const [books, setBooks] = useState([]);
    const [presets, setPresets] = useState({});
    const [costs, setCosts] = useState({});
    const [library, setLibrary] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [activeTab, setActiveTab] = useState('generate');
    
    // Generation form state
    const [selectedBook, setSelectedBook] = useState(null);
    const [coverType, setCoverType] = useState('front');
    const [format, setFormat] = useState('ebook');
    const [genre, setGenre] = useState('');
    const [customPrompt, setCustomPrompt] = useState('');
    const [backBlurb, setBackBlurb] = useState('');
    const [authorBio, setAuthorBio] = useState('');
    const [pageCount, setPageCount] = useState(200);
    
    // Generated cover preview
    const [generatedCover, setGeneratedCover] = useState(null);

    const userTier = user?.subscription_tier || 'free';

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const [booksRes, presetsRes, costsRes, libraryRes] = await Promise.all([
                booksApi.list(),
                coversApi.getPresets(),
                coversApi.getCosts(),
                coversApi.getLibrary()
            ]);
            
            setBooks(booksRes.data || []);
            setPresets(presetsRes.data.presets || {});
            setCosts(costsRes.data || {});
            setLibrary(libraryRes.data.covers || []);
        } catch (error) {
            console.error('Failed to load data:', error);
            toast.error('Failed to load cover designer data');
        } finally {
            setLoading(false);
        }
    };

    const canUseCoverType = (type) => {
        const tierHierarchy = ['free', 'writer', 'author', 'studio'];
        const coverConfig = COVER_TYPES.find(c => c.id === type);
        const userTierIndex = tierHierarchy.indexOf(userTier);
        const requiredTierIndex = tierHierarchy.indexOf(coverConfig?.minTier || 'free');
        return userTierIndex >= requiredTierIndex;
    };

    const canUseFormat = (formatId) => {
        if (userTier === 'free') return formatId === 'ebook';
        if (userTier === 'writer') return ['ebook', 'paperback_6x9', 'paperback_5x8'].includes(formatId);
        return true; // author and studio can use all
    };

    const handleGenerate = async () => {
        if (!selectedBook) {
            toast.error('Please select a book');
            return;
        }

        if (!canUseCoverType(coverType)) {
            toast.error(`Upgrade to Author or Studio to generate ${coverType} covers`);
            return;
        }

        setGenerating(true);
        try {
            const response = await coversApi.generate({
                book_id: selectedBook.id,
                cover_type: coverType,
                title: selectedBook.title,
                subtitle: selectedBook.subtitle || '',
                author_name: user?.name || 'Unknown Author',
                genre: genre || selectedBook.genre || 'Literary',
                description: selectedBook.description || '',
                page_count: pageCount,
                format: format,
                custom_prompt: customPrompt || null,
                back_blurb: backBlurb || null,
                author_bio: authorBio || null
            });

            setGeneratedCover(response.data);
            toast.success('Cover generated successfully!');
            
            // Refresh library
            const libraryRes = await coversApi.getLibrary();
            setLibrary(libraryRes.data.covers || []);
            
        } catch (error) {
            const errorMsg = error.response?.data?.detail || 'Failed to generate cover';
            toast.error(errorMsg);
        } finally {
            setGenerating(false);
        }
    };

    const handleSetPrimary = async (coverId) => {
        try {
            await coversApi.setPrimary(coverId);
            toast.success('Cover set as primary!');
            loadInitialData();
        } catch (error) {
            toast.error('Failed to set primary cover');
        }
    };

    const handleDeleteCover = async (coverId) => {
        if (!window.confirm('Delete this cover?')) return;
        
        try {
            await coversApi.delete(coverId);
            toast.success('Cover deleted');
            setLibrary(prev => prev.filter(c => c.id !== coverId));
        } catch (error) {
            toast.error('Failed to delete cover');
        }
    };

    const handleDownload = async (imageUrl, title) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}_cover.png`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Failed to download cover');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={onBack} data-testid="back-btn">
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back
                        </Button>
                        <div>
                            <h1 className="font-serif text-2xl font-semibold flex items-center gap-2">
                                <Palette className="h-6 w-6 text-accent" />
                                Cover Designer
                            </h1>
                            <p className="text-sm text-muted-foreground">Create professional book covers with AI</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Badge variant="outline" className="flex items-center gap-1">
                            <Sparkles className="h-3 w-3" />
                            {user?.credits || 0} credits
                        </Badge>
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => onNavigate && onNavigate('credits')}
                            data-testid="covers-buy-credits"
                        >
                            Buy Credits
                        </Button>
                        <Badge className="bg-accent">{userTier}</Badge>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-8">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="generate" data-testid="tab-generate">
                            <Wand2 className="h-4 w-4 mr-2" />
                            Generate Cover
                        </TabsTrigger>
                        <TabsTrigger value="library" data-testid="tab-library">
                            <Image className="h-4 w-4 mr-2" />
                            My Covers ({library.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="generate">
                        <div className="grid lg:grid-cols-2 gap-8">
                            {/* Generation Form */}
                            <div className="space-y-6">
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="font-serif text-lg">Cover Settings</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        {/* Book Selection */}
                                        <div>
                                            <Label>Select Book *</Label>
                                            <Select
                                                value={selectedBook?.id || ''}
                                                onValueChange={(id) => {
                                                    const book = books.find(b => b.id === id);
                                                    setSelectedBook(book);
                                                    if (book?.genre) setGenre(book.genre.toLowerCase());
                                                }}
                                            >
                                                <SelectTrigger data-testid="book-select">
                                                    <SelectValue placeholder="Choose a book" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {books.map(book => (
                                                        <SelectItem key={book.id} value={book.id}>
                                                            {book.title}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Cover Type */}
                                        <div>
                                            <Label>Cover Type</Label>
                                            <div className="grid grid-cols-2 gap-2 mt-2">
                                                {COVER_TYPES.map(type => {
                                                    const Icon = type.icon;
                                                    const canUse = canUseCoverType(type.id);
                                                    return (
                                                        <button
                                                            key={type.id}
                                                            onClick={() => canUse && setCoverType(type.id)}
                                                            disabled={!canUse}
                                                            className={`p-3 rounded-lg border text-left transition-all ${
                                                                coverType === type.id
                                                                    ? 'border-accent bg-accent/10'
                                                                    : canUse 
                                                                        ? 'border-border hover:border-accent/50'
                                                                        : 'border-border opacity-50 cursor-not-allowed'
                                                            }`}
                                                            data-testid={`cover-type-${type.id}`}
                                                        >
                                                            <div className="flex items-center justify-between mb-1">
                                                                <Icon className="h-4 w-4 text-accent" />
                                                                {!canUse && <Lock className="h-3 w-3 text-muted-foreground" />}
                                                            </div>
                                                            <div className="font-medium text-sm">{type.name}</div>
                                                            <div className="text-xs text-muted-foreground">{type.cost} credits</div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Format */}
                                        <div>
                                            <Label>Format</Label>
                                            <Select value={format} onValueChange={setFormat}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {FORMAT_OPTIONS.map(fmt => (
                                                        <SelectItem 
                                                            key={fmt.id} 
                                                            value={fmt.id}
                                                            disabled={!canUseFormat(fmt.id)}
                                                        >
                                                            <div className="flex items-center justify-between w-full">
                                                                <span>{fmt.name}</span>
                                                                {!canUseFormat(fmt.id) && <Lock className="h-3 w-3 ml-2" />}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Genre */}
                                        <div>
                                            <Label>Genre Style</Label>
                                            <Select value={genre} onValueChange={setGenre}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select genre style" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {GENRE_OPTIONS.map(g => (
                                                        <SelectItem key={g.toLowerCase()} value={g.toLowerCase()}>
                                                            {g}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {genre && presets[genre] && (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Style: {presets[genre].style?.slice(0, 60)}...
                                                </p>
                                            )}
                                        </div>

                                        {/* Page Count for spine calculation */}
                                        {(coverType === 'spine' || coverType === 'full') && format !== 'ebook' && (
                                            <div>
                                                <Label>Page Count (for spine width)</Label>
                                                <Input
                                                    type="number"
                                                    value={pageCount}
                                                    onChange={(e) => setPageCount(parseInt(e.target.value) || 200)}
                                                    min={50}
                                                    max={1000}
                                                />
                                            </div>
                                        )}

                                        {/* Custom Prompt */}
                                        <div>
                                            <Label>Custom Style Prompt (Optional)</Label>
                                            <Textarea
                                                value={customPrompt}
                                                onChange={(e) => setCustomPrompt(e.target.value)}
                                                placeholder="Describe specific visual elements, mood, or style..."
                                                rows={2}
                                            />
                                        </div>

                                        {/* Back cover specific fields */}
                                        {(coverType === 'back' || coverType === 'full') && (
                                            <>
                                                <div>
                                                    <Label>Back Cover Blurb</Label>
                                                    <Textarea
                                                        value={backBlurb}
                                                        onChange={(e) => setBackBlurb(e.target.value)}
                                                        placeholder="Enter your book's back cover blurb..."
                                                        rows={3}
                                                    />
                                                </div>
                                                <div>
                                                    <Label>Author Bio</Label>
                                                    <Textarea
                                                        value={authorBio}
                                                        onChange={(e) => setAuthorBio(e.target.value)}
                                                        placeholder="Brief author biography..."
                                                        rows={2}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </CardContent>
                                </Card>

                                <Button 
                                    onClick={handleGenerate}
                                    disabled={generating || !selectedBook}
                                    className="w-full"
                                    size="lg"
                                    data-testid="generate-cover-btn"
                                >
                                    {generating ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Generating Cover...
                                        </>
                                    ) : (
                                        <>
                                            <Wand2 className="h-4 w-4 mr-2" />
                                            Generate {COVER_TYPES.find(t => t.id === coverType)?.name} ({COVER_TYPES.find(t => t.id === coverType)?.cost} credits)
                                        </>
                                    )}
                                </Button>
                            </div>

                            {/* Preview Panel */}
                            <div>
                                <Card className="sticky top-6">
                                    <CardHeader>
                                        <CardTitle className="font-serif text-lg">Preview</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {generatedCover ? (
                                            <div className="space-y-4">
                                                <div className="relative aspect-[2/3] bg-muted rounded-lg overflow-hidden">
                                                    <img
                                                        src={generatedCover.image_url}
                                                        alt="Generated cover"
                                                        className="w-full h-full object-cover"
                                                    />
                                                    {generatedCover.is_watermarked && (
                                                        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                                                            <Badge variant="destructive">Preview - Upgrade to remove watermark</Badge>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="outline"
                                                        className="flex-1"
                                                        onClick={() => handleDownload(generatedCover.image_url, selectedBook?.title || 'cover')}
                                                    >
                                                        <Download className="h-4 w-4 mr-2" />
                                                        Download
                                                    </Button>
                                                    <Button
                                                        className="flex-1"
                                                        onClick={() => handleSetPrimary(generatedCover.cover_id)}
                                                    >
                                                        <Star className="h-4 w-4 mr-2" />
                                                        Set as Primary
                                                    </Button>
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    <p>Credits used: {generatedCover.credits_used}</p>
                                                    <p>Remaining: {generatedCover.credits_remaining}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="aspect-[2/3] bg-muted rounded-lg flex items-center justify-center">
                                                <div className="text-center text-muted-foreground">
                                                    <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                                    <p>Your generated cover will appear here</p>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Tier Info */}
                                <Card className="mt-4">
                                    <CardContent className="pt-4">
                                        <h4 className="font-medium mb-2 flex items-center gap-2">
                                            <Crown className="h-4 w-4 text-accent" />
                                            Your Plan: {userTier.charAt(0).toUpperCase() + userTier.slice(1)}
                                        </h4>
                                        <ul className="text-sm text-muted-foreground space-y-1">
                                            {userTier === 'free' && (
                                                <>
                                                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> Front covers only</li>
                                                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> eBook format</li>
                                                    <li className="flex items-center gap-2"><Lock className="h-3 w-3" /> Upgrade for full covers</li>
                                                </>
                                            )}
                                            {userTier === 'writer' && (
                                                <>
                                                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> Front covers (no watermark)</li>
                                                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> eBook + Paperback formats</li>
                                                    <li className="flex items-center gap-2"><Lock className="h-3 w-3" /> Upgrade for full covers</li>
                                                </>
                                            )}
                                            {(userTier === 'author' || userTier === 'studio') && (
                                                <>
                                                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> All cover types</li>
                                                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> All formats including hardcover</li>
                                                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> Full wraparound covers</li>
                                                </>
                                            )}
                                        </ul>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="library">
                        {library.length === 0 ? (
                            <Card className="text-center py-12">
                                <CardContent>
                                    <Image className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                    <h3 className="font-serif text-xl mb-2">No Covers Yet</h3>
                                    <p className="text-muted-foreground mb-4">
                                        Generate your first cover to see it here.
                                    </p>
                                    <Button onClick={() => setActiveTab('generate')}>
                                        <Wand2 className="h-4 w-4 mr-2" />
                                        Generate Cover
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {library.map(cover => {
                                    const book = books.find(b => b.id === cover.book_id);
                                    return (
                                        <Card key={cover.id} className="overflow-hidden group" data-testid={`cover-${cover.id}`}>
                                            <div className="relative aspect-[2/3]">
                                                <img
                                                    src={cover.image_url}
                                                    alt={cover.metadata?.title || 'Book cover'}
                                                    className="w-full h-full object-cover"
                                                />
                                                {cover.is_primary && (
                                                    <Badge className="absolute top-2 left-2 bg-accent">
                                                        <Star className="h-3 w-3 mr-1" />
                                                        Primary
                                                    </Badge>
                                                )}
                                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        onClick={() => handleDownload(cover.image_url, cover.metadata?.title || 'cover')}
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                    {!cover.is_primary && (
                                                        <Button
                                                            size="sm"
                                                            variant="secondary"
                                                            onClick={() => handleSetPrimary(cover.id)}
                                                        >
                                                            <Star className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="destructive"
                                                        onClick={() => handleDeleteCover(cover.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            <CardContent className="p-3">
                                                <p className="font-medium text-sm truncate">{cover.metadata?.title || 'Untitled'}</p>
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                    <Badge variant="outline" className="text-xs">{cover.cover_type}</Badge>
                                                    <span>{cover.format}</span>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
};

export default CoverDesigner;
